import './Main.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { machineRankingQuery, WizardData } from './Model'
import { MachineRankingExports } from './Exports'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { TimeDHM } from '@/components/Elements/time'
import { secondsToDHM } from '@/common/time'
import { MachineName } from '@/components/machine'
import { Order, OrderManager } from '@/components/order/Order'
import { TimeZoneNotice } from '@/components/timezone/TimeZoneNotice'

export interface MachineRange {
  machineId: string;
  manufacturer: string;
  category: string;
  model: string;
  internalNumber: string;
  onFireRatio: number;
  totalActive: number;
  totalOnFire: number;
  totalBuckets: number;
  minMth?: number | null;
  maxMth?: number | null;
}

const tenMinInSec = 60 * 10
const MachineRangeOrderCols = ['internalNumber', 'onFireRatio', 'totalOnFire', 'totalActive', 'diffMth']
const MachineRangeDefaultOrder: Order = {
  key: 'onFireRatio',
  asc: false
}

export const MachinesReportWizardResult = () => {
  const { services, translates } = useAppContext()
  const { wizardData, prevStep } = useWizard<WizardData>()
  const [data, setData] = useState<MachineRange[]>()
  const [failed, setFailed] = useState(false)
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 5
  })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    MachineRangeOrderCols,
    MachineRangeDefaultOrder,
    (order: Order) => {
      setOrderManager(current => current.newManager(order))
      setPagginationState(current => ({ ...current, pageNumber: 1 }))
    },
    {
      diffMth: (a: MachineRange, b: MachineRange, asc: boolean) => {
        const aValue = a.maxMth != null && a.minMth != null ? a.maxMth - a.minMth : null
        const bValue = b.maxMth != null && b.minMth != null ? b.maxMth - b.minMth : null
        const direction = asc ? 1 : -1
        if (aValue === bValue) return 0
        if (aValue === null) return direction
        if (bValue === null) return -direction
        return (aValue > bValue ? 1 : -1) * direction
      }
    }
  ))

  function refresh() {
    setFailed(false)
    services.session.fetchWithToken(`/api/communal/my/reports/machinesranking?${machineRankingQuery(wizardData)}`).then(response => {
      if (!response.ok) throw new Error('Cannot load ranking')
      return response.json()
    }).then((data: MachineRange[]) => {
      setData((data || []).sort((a, b) => b.onFireRatio - a.onFireRatio))
    }).catch(() => setFailed(true))
  }

  useEffect(refresh, [])

  const orderedData = useMemo(() => {
    return orderManager.orderListByKey(data || [])
  }, [data, orderManager])

  if (failed) return <div className='ranking-selection'>
    <p role='alert'>{translates?.for('report.machinesranking.selection.load_error')}</p>
    <button type='button' className='btn btn-next' onClick={prevStep}>{translates?.for('report.machinesranking.selection.back')}</button>
    <button type='button' className='btn btn-next' onClick={refresh}>{translates?.for('report.machinesranking.selection.retry')}</button>
  </div>

  if (translates === undefined || wizardData === undefined || data === undefined) {
    return <div className='spinner'></div>
  }


  function headerRow(key: string, transKey?: string, sortable = false) {
    if (translates === undefined || wizardData === undefined) {
      return null
    }
    transKey = transKey ? transKey : key
    return <div className='hcol' onClick={sortable ? () => orderManager.click(key) : undefined}>
      {translates.for('report.machinesranking.summary.table.' + transKey)}
      {sortable && orderManager.orderInfoElement(key)}
    </div>
  }

  const renderTable = (
    data: MachineRange[],
    state: StaticPaginatedTableState,
    setPagginationState: React.Dispatch<React.SetStateAction<StaticPaginatedTableState>> | undefined,
    sortable = false
  ) => {
    return <StaticPaginatedTable<MachineRange>
      data={data}
      parentState={state}
      setParentState={setPagginationState}
      header={() => <>
        {headerRow('internalNumber', 'machinename', sortable)}
        {headerRow('onFireRatio', 'on_fire_ratio', sortable)}
        {headerRow('totalOnFire', 'total_on_fire', sortable)}
        {headerRow('totalActive', 'total_active', sortable)}
        {headerRow('diffMth', 'diff_mth', sortable)}


        {/* "on_fire_ratio": "Procentowy czas pracy",
                    "total_active": "Aktywny czas",
                    "total_on_fire": "Czas pracy",
                    "min_mth": "Początkowa liczba roboczogodzin",
                    "max_mth": "Końcowy liczba roboczogodzin" */}
      </>}
      row={(row) => {
        const openDetails = () => {
          const url = `${window.location.origin}/communal/statistics/machines?length=1d&machine=${row.machineId}`;
          window.open(url, '_blank');
        }
        return <>
          <div className={`col`} onClick={openDetails}>
            <MachineName row={{
              internalNumber: row.internalNumber,
              type: {
                category: row.category,
                manufacturer: row.manufacturer,
                model: row.model
              }
            }} />
          </div>
          <div className={`col`} onClick={openDetails}>{(row.onFireRatio * 100).toFixed(2)}%</div>
          <div className={`col`} onClick={openDetails}>
            <TimeDHM dhm={secondsToDHM(row.totalOnFire)} translates={translates} />
          </div>
          <div className={`col`} onClick={openDetails}>
            <TimeDHM dhm={secondsToDHM(row.totalActive)} translates={translates} />
          </div>
          <div className={`col`} onClick={openDetails}>{row.maxMth && row.minMth ? row.maxMth - row.minMth : '-'}</div>
        </>
      }}
      translates={translates}
    />
  }

  return <>
    <div>
      <h2>{translates.for('report.machinesranking.summary.title')}</h2>
      <TimeZoneNotice />

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={() => {
          orderManager.setCurrentOrderKey('totalActive')
          orderManager.setDirection(false)
        }}>{translates.for('report.machinesranking.summary.best')}</button>
        <button type="button" className='btn btn-next' onClick={() => {
          orderManager.setCurrentOrderKey('totalActive')
          orderManager.setDirection(true)
        }}>{translates.for('report.machinesranking.summary.worst')}</button>
      </div>

      <h3>{translates.for('report.machinesranking.summary.all_title')}</h3>
      {renderTable(orderedData, pagginationState, setPagginationState, true)}

      <div className='right-btns right-btns-margin'>
        <button type='button' className='btn btn-next' onClick={prevStep}>{translates.for('report.machinesranking.selection.back')}</button>
        <MachineRankingExports />
      </div>
    </div>
  </>
}
